/**
 * ScenarioMotion - Scene 기반 범용 모션 컴포넌트
 * 시나리오 파일을 받아서 자동으로 애니메이션 실행
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { highlightLayer } from "@/utils";
import "@/utils/svgHighlight.css";
import "./ScenarioMotion.css";
import type {
  MotionScenario,
  SceneConfig,
  ScenarioMotionProps,
  SvgElementChange,
  ViewBoxConfig,
} from "./types";

const ScenarioMotion: React.FC<ScenarioMotionProps> = ({
  scenario,
  title,
  description,
  isHovered: externalIsHovered,
}) => {
  const [activeSceneIndex, setActiveSceneIndex] = useState(0);
  const [viewBoxValues, setViewBoxValues] = useState({
    x: 0,
    y: 0,
    width: 1920,
    height: 1080,
  });
  const [originalViewBox, setOriginalViewBox] = useState<string>("");
  const [repeatCount, setRepeatCount] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const effectiveIsHovered = externalIsHovered ?? false;

  // 기본값 설정
  const transitionSpeed = scenario.transitionSpeed ?? 1500;
  const repeatConfig = scenario.repeat ?? { mode: "once" };
  const viewBoxAnimationDuration = scenario.viewBoxAnimation?.duration ?? 800;

  // SVG 마운트 시 원본 viewBox 저장
  useEffect(() => {
    if (svgRef.current && !originalViewBox) {
      const vb = svgRef.current.getAttribute("viewBox");
      if (vb) {
        setOriginalViewBox(vb);
        const [x, y, width, height] = vb.split(" ").map(Number);
        setViewBoxValues({ x, y, width, height });
      } else {
        const width = svgRef.current.width.baseVal.value || 1920;
        const height = svgRef.current.height.baseVal.value || 1080;
        const calculatedViewBox = `0 0 ${width} ${height}`;
        setOriginalViewBox(calculatedViewBox);
        setViewBoxValues({ x: 0, y: 0, width, height });
      }
    }
  }, [originalViewBox]);

  // SVG 요소에 스타일 적용
  const applyElementChanges = useCallback((changes: SvgElementChange[]) => {
    if (!svgRef.current) return;

    changes.forEach((change) => {
      const elements = svgRef.current!.querySelectorAll(change.selector);
      elements.forEach((element) => {
        const el = element as SVGElement;

        if (change.opacity !== undefined) {
          el.style.opacity = change.opacity.toString();
        }

        const transforms: string[] = [];
        if (
          change.translateX !== undefined ||
          change.translateY !== undefined
        ) {
          const tx = change.translateX ?? 0;
          const ty = change.translateY ?? 0;
          transforms.push(`translate(${tx}px, ${ty}px)`);
        }
        if (change.scale !== undefined) {
          transforms.push(`scale(${change.scale})`);
        }

        if (transforms.length > 0) {
          el.style.transform = transforms.join(" ");
        }
      });
    });
  }, []);

  // ViewBox 계산 및 적용
  const applyViewBox = useCallback(
    (viewBoxConfig?: ViewBoxConfig) => {
      if (!svgRef.current || !originalViewBox) return;

      // ViewBox 설정이 없으면 원본으로 복원
      if (!viewBoxConfig) {
        const [x, y, width, height] = originalViewBox.split(" ").map(Number);
        setViewBoxValues({ x, y, width, height });
        return;
      }

      // 직접 지정된 경우
      if (
        viewBoxConfig.x !== undefined &&
        viewBoxConfig.y !== undefined &&
        viewBoxConfig.width !== undefined &&
        viewBoxConfig.height !== undefined
      ) {
        setViewBoxValues({
          x: viewBoxConfig.x,
          y: viewBoxConfig.y,
          width: viewBoxConfig.width,
          height: viewBoxConfig.height,
        });
        return;
      }

      // centerSelector로 계산
      if (viewBoxConfig.centerSelector) {
        const element = svgRef.current.querySelector(
          viewBoxConfig.centerSelector
        ) as SVGGraphicsElement;

        if (element) {
          try {
            const bbox = element.getBBox();
            const [origX, origY, origWidth, origHeight] = originalViewBox
              .split(" ")
              .map(Number);

            const zoom = viewBoxConfig.zoom ?? 0.4;
            const newWidth = origWidth * zoom;
            const newHeight = origHeight * zoom;

            const layerCenterX = bbox.x + bbox.width / 2;
            const layerCenterY = bbox.y + bbox.height / 2;

            const newX = layerCenterX - newWidth / 2;
            const newY = layerCenterY - newHeight / 2;

            setViewBoxValues({
              x: newX,
              y: newY,
              width: newWidth,
              height: newHeight,
            });
          } catch (error) {
            console.error("ViewBox 계산 실패:", error);
          }
        }
      }
    },
    [originalViewBox]
  );

  // Scene 적용
  const applyScene = useCallback(
    (scene: SceneConfig) => {
      // 요소 변경 적용
      applyElementChanges(scene.elements);

      // ViewBox 적용
      setTimeout(() => {
        applyViewBox(scene.viewBox);
      }, 100);

      // 글로우 효과 적용
      if (scene.glowEffect && scene.glowSelector) {
        setTimeout(() => {
          if (svgRef.current) {
            const element = svgRef.current.querySelector(scene.glowSelector!);
            if (element) {
              element.classList.add("svg-glow");
              setTimeout(() => {
                element.classList.remove("svg-glow");
              }, 1200);
            }
          }
        }, 100);
      }
    },
    [applyElementChanges, applyViewBox]
  );

  // 애니메이션 시작
  const startAnimation = useCallback(() => {
    setActiveSceneIndex(0);
    setRepeatCount(0);

    // 첫 Scene 적용
    if (scenario.scenes.length > 0) {
      applyScene(scenario.scenes[0]);
    }

    let currentIndex = 0;

    intervalRef.current = setInterval(() => {
      currentIndex++;

      if (currentIndex >= scenario.scenes.length) {
        // Scene이 모두 끝났을 때
        if (repeatConfig.mode === "once") {
          // 한 번만 실행
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        } else if (repeatConfig.mode === "count") {
          // 지정 횟수만큼 반복
          setRepeatCount((prev) => {
            const newCount = prev + 1;
            if (newCount >= (repeatConfig.count ?? 1)) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              return prev;
            }
            currentIndex = 0;
            return newCount;
          });
        } else {
          // 무한 반복
          currentIndex = 0;
        }
      }

      setActiveSceneIndex(currentIndex);
      if (scenario.scenes[currentIndex]) {
        applyScene(scenario.scenes[currentIndex]);
      }
    }, transitionSpeed);
  }, [scenario, repeatConfig, transitionSpeed, applyScene]);

  // 호버 시 애니메이션 제어
  useEffect(() => {
    if (effectiveIsHovered) {
      startAnimation();
    } else {
      // 호버 해제 시 정리
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setActiveSceneIndex(0);
      setRepeatCount(0);

      // 초기 상태로 복원
      if (originalViewBox) {
        const [x, y, width, height] = originalViewBox.split(" ").map(Number);
        setViewBoxValues({ x, y, width, height });
      }

      // 모든 요소 초기화
      if (svgRef.current) {
        const allElements = svgRef.current.querySelectorAll("[style]");
        allElements.forEach((el) => {
          (el as HTMLElement).style.opacity = "";
          (el as HTMLElement).style.transform = "";
        });
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [effectiveIsHovered, originalViewBox, startAnimation]);

  // ViewBox 애니메이션
  useEffect(() => {
    if (svgRef.current) {
      const targetViewBox = `${viewBoxValues.x} ${viewBoxValues.y} ${viewBoxValues.width} ${viewBoxValues.height}`;
      const currentViewBox =
        svgRef.current.getAttribute("viewBox") || originalViewBox;

      if (currentViewBox !== targetViewBox) {
        const [startX, startY, startW, startH] = currentViewBox
          .split(" ")
          .map(Number);
        const { x: endX, y: endY, width: endW, height: endH } = viewBoxValues;

        const startTime = performance.now();

        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / viewBoxAnimationDuration, 1);

          const easeProgress =
            progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          const x = startX + (endX - startX) * easeProgress;
          const y = startY + (endY - startY) * easeProgress;
          const w = startW + (endW - startW) * easeProgress;
          const h = startH + (endH - startH) * easeProgress;

          svgRef.current?.setAttribute("viewBox", `${x} ${y} ${w} ${h}`);

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };

        requestAnimationFrame(animate);
      }
    }
  }, [viewBoxValues, originalViewBox, viewBoxAnimationDuration]);

  const SvgComponent = scenario.svgComponent;

  return (
    <div className="scenario-motion-container">
      <div className="scenario-motion-content">
        <div className="scenario-svg-wrapper">
          <SvgComponent
            ref={svgRef}
            className={`scenario-svg ${
              effectiveIsHovered ? "hovering" : ""
            } active-scene-${activeSceneIndex}`}
            preserveAspectRatio="xMidYMid meet"
          />
        </div>
        <div className="scenario-text">
          <h3 className="scenario-title">{title}</h3>
          <p className="scenario-description">{description}</p>
        </div>
      </div>
    </div>
  );
};

export default ScenarioMotion;
